/**
 * 이용권 관리 API (어드민)
 *
 * 백엔드 명세: BACKEND_API_REQUIREMENTS_DAILY_RESERVATIONS_AND_TICKETS.md
 * - GET    /api/admin/tickets               목록 조회
 * - GET    /api/admin/tickets/{seq}         상세 (요약 + 사용/취소 이력)
 * - POST   /api/admin/tickets               수동 발급 (OFFLINE)
 * - POST   /api/admin/tickets/uses/{seq}/cancel  사용분 환원
 *
 * 매핑 DB: HIST_PURCHASE_TICKET / HIST_TICKET_USE / HIST_TICKET_CANCEL /
 *         HIST_RESERVATION_DETAIL / INFO_PRODUCT_TICKET
 */

import { apiClient } from './client';
import type { ReservType, ReservStatus } from './dailyReservations';

// ─────────────────────────────────────────────────────────────
// 공용 enum 타입
// ─────────────────────────────────────────────────────────────

export type TicketProductType =
  | 'PRODUCT_TYPE_DAYCARE'
  | 'PRODUCT_TYPE_HOTEL'
  | 'PRODUCT_TYPE_CLASS'
  | 'PRODUCT_TYPE_GROOMING'
  | 'PRODUCT_TYPE_KINDERGARTEN';

export type TicketPaymentType = 'ONLINE' | 'OFFLINE';
/**
 * HIST_PURCHASE_TICKET.PAYMENT_STATUS 실제 값.
 * - COMPLETED: 온라인 PG 결제완료 / PAID: 어드민 수동발급 / REFUND: 결제취소(환불)
 */
export type TicketPaymentStatus = 'PENDING' | 'PAID' | 'COMPLETED' | 'REFUND';

/**
 * 이용권 운영 상태 (BE 가 계산해서 내려주는 파생값).
 * REVOKED(회수)는 LOG_MODIFIED_TICKET 최신 상태 기준이며 다른 상태보다 우선한다.
 */
export type TicketStatus = 'ACTIVE' | 'EXHAUSTED' | 'EXPIRED' | 'REVOKED';

// ─────────────────────────────────────────────────────────────
// 응답 모델
// ─────────────────────────────────────────────────────────────

/** 발급(구매) 1건 - HIST_PURCHASE_TICKET 1행 */
export interface PurchaseTicketRow {
  purchaseTicketSeq: number;
  orderId: string;
  paymentKey: string | null;
  paymentStatus: TicketPaymentStatus;
  /** ONLINE: 사용자 결제 / OFFLINE: 어드민 수동 발급 */
  paymentType: TicketPaymentType;
  userSeq: number;
  userName: string;
  userPhone: string;
  petSeq: number | null;
  petName: string | null;
  productTicketSeq: number;
  productType: TicketProductType;
  companySeq: number;
  companyName: string;
  name: string;
  contents: string;
  cost: number;
  discount: number;
  amount: number;
  splitYn: 'Y' | 'N';
  /** 총 횟수 (호텔은 박수, 다회권 미적용 시 1) */
  splitCount: number;
  /** YYYY-MM-DD HH:mm:ss */
  dtUsable: string;
  dtExpired: string;
  dtRegist: string;
}

/** 목록/상세 응답에 포함되는 집계 정보 */
export interface TicketSummary extends PurchaseTicketRow {
  /** 사용된 총 횟수 (취소 반영 후) */
  usedQty: number;
  /** 취소된 총 횟수 */
  canceledQty: number;
  /** 잔여 횟수 (호텔은 사용 완료 시 0, 회수 건은 0) */
  remainingQty: number;
  ticketStatus: TicketStatus;
  /** 회수 일시 (YYYY-MM-DD HH:mm:ss). 회수 건이 아니면 null */
  revokedAt: string | null;
  /** 만료까지 남은 일수 (음수면 만료) */
  daysToExpire: number;
  /** 결제취소(환불) 가능 여부 (온라인 결제 + 결제완료 + 미사용일 때 true) */
  refundable: boolean;
  /** 환불 불가 사유 (refundable=true이면 null) */
  refundableReason: string | null;
}

/** 사용 row 와 연관된 예약(예약상세) 표시용 정보 */
export interface TicketUseReservationInfo {
  reservationSeq: number;
  reservDetailSeq: number;
  companySeq: number;
  companyName: string;
  reservType: ReservType;
  /** HIST_RESERVATION_DETAIL.DT_EXPECTED - YYYY-MM-DD */
  dtExpected: string;
  /** HIST_RESERVATION_DETAIL.CD_RESERV_STATUS */
  status: ReservStatus;
}

/** 사용 1건 - HIST_TICKET_USE */
export interface TicketUseRow {
  ticketUseSeq: number;
  purchaseTicketSeq: number;
  reservDetailSeq: number | null;
  qty: number;
  /** YYYY-MM-DD HH:mm:ss */
  dtRegist: string;
  /** BE 가 JOIN 으로 함께 내려줌. 비정상 row 인 경우 null 가능. */
  reservation: TicketUseReservationInfo | null;
}

/** 취소(환원) 1건 - HIST_TICKET_CANCEL */
export interface TicketCancelRow {
  ticketCancelSeq: number;
  purchaseTicketSeq: number;
  ticketUseSeq: number;
  reservDetailSeq: number | null;
  qty: number;
  /** YYYY-MM-DD HH:mm:ss */
  dtRegist: string;
}

export interface TicketDetailResponse {
  summary: TicketSummary;
  uses: TicketUseRow[];
  cancels: TicketCancelRow[];
}

// ─────────────────────────────────────────────────────────────
// 요청 파라미터 / Body
// ─────────────────────────────────────────────────────────────

export interface GetTicketsParams {
  /** 회원/펫/사업장명/이용권명/주문번호 부분 일치 */
  keyword?: string;
  /** 지정 시 해당 회원이 보유한 이용권만 조회 (회원 상세 화면용) */
  userSeq?: number;
  status?: 'ALL' | TicketStatus;
  productType?: 'ALL' | TicketProductType;
  paymentType?: 'ALL' | TicketPaymentType;
  /** 발급일자 (YYYY-MM-DD). 해당 일자에 발급된 이용권만 조회. */
  dtRegist?: string;
}

/** 어드민 수동 발급 요청 body */
export interface TicketIssueInput {
  userSeq: number;
  userName: string;
  userPhone: string;
  petSeq: number | null;
  petName: string | null;
  productTicketSeq: number;
  productType: TicketProductType;
  companySeq: number;
  companyName: string;
  name: string;
  contents: string;
  cost: number;
  discount: number;
  amount: number;
  splitYn: 'Y' | 'N';
  splitCount: number;
  /** YYYY-MM-DD HH:mm:ss */
  dtUsable: string;
  dtExpired: string;
  /** 어드민 메모 */
  memo?: string;
}

// ─────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────

function unwrap<T>(resp: { data?: unknown }): T {
  const raw = resp.data as { data?: T } | T | undefined;
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in (raw as Record<string, unknown>)
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

export const ticketsApi = {
  /** 이용권 목록 조회 (요약/잔여 횟수 포함) */
  getTickets: async (
    params: GetTicketsParams = {},
  ): Promise<TicketSummary[]> => {
    const response = await apiClient.get('/admin/tickets', {
      params: {
        keyword: params.keyword,
        userSeq: params.userSeq,
        status: params.status ?? 'ALL',
        productType: params.productType ?? 'ALL',
        paymentType: params.paymentType ?? 'ALL',
        dtRegist: params.dtRegist || undefined,
      },
    });
    return unwrap<TicketSummary[]>(response);
  },

  /** 이용권 1건 상세 (요약 + 사용/취소 이력) */
  getTicketDetail: async (
    purchaseTicketSeq: number,
  ): Promise<TicketDetailResponse> => {
    const response = await apiClient.get(`/admin/tickets/${purchaseTicketSeq}`);
    return unwrap<TicketDetailResponse>(response);
  },

  /** 어드민 수동 발급 (OFFLINE) */
  issueTicket: async (input: TicketIssueInput): Promise<PurchaseTicketRow> => {
    const response = await apiClient.post('/admin/tickets', input);
    return unwrap<PurchaseTicketRow>(response);
  },

  /**
   * 사용분 환원.
   * - 연관 예약상세가 REQUESTED/UPDATED/CONFIRMED 일 때만 BE 가 허용.
   * - 호텔 이용권은 qty 무시, 잔량 전체 환원.
   * - 환원 시 BE 가 동일 트랜잭션으로 연결 예약을 CANCELED 로 변경.
   */
  cancelTicketUse: async (params: {
    ticketUseSeq: number;
    qty?: number;
    reason?: string;
  }): Promise<TicketCancelRow> => {
    const response = await apiClient.post(
      `/admin/tickets/uses/${params.ticketUseSeq}/cancel`,
      { qty: params.qty, reason: params.reason },
    );
    return unwrap<TicketCancelRow>(response);
  },

  /**
   * 이용권 결제취소(환불).
   * - 온라인 결제건만 가능. 토스 부분취소로 해당 이용권 1건 금액만 환불.
   * - 사용된 이용권/오프라인 발급/이미 환불된 건은 BE 가 거부(HTTP 400 + 사유 메시지).
   */
  refundTicket: async (params: {
    purchaseTicketSeq: number;
    reason?: string;
  }): Promise<TicketSummary> => {
    const response = await apiClient.post(
      `/admin/tickets/${params.purchaseTicketSeq}/refund`,
      { reason: params.reason },
    );
    return unwrap<TicketSummary>(response);
  },
};
